import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, inr } from "../lib/api";
import Shell, { PageHeader } from "../components/Shell";
import { Barcode, Printer, Plus, Minus, Trash } from "@phosphor-icons/react";

export default function Barcodes() {
  const { storeId } = useParams();
  const [inv, setInv] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState({}); // sku -> count

  const load = async () => {
    const r = await api.get(`/store/${storeId}/inventory?q=${encodeURIComponent(q)}`);
    setInv(r.data);
  };
  useEffect(() => { load(); }, [storeId]);

  const filtered = inv.filter((s) => {
    if (!q) return true;
    const qs = q.toLowerCase();
    return (s.sku || "").toLowerCase().includes(qs) || (s.product_name || "").toLowerCase().includes(qs) || (s.barcode || "").toLowerCase().includes(qs);
  });

  const inc = (sku, delta) => {
    const cur = selected[sku] || 0;
    const next = Math.max(0, cur + delta);
    const copy = { ...selected };
    if (next === 0) delete copy[sku]; else copy[sku] = next;
    setSelected(copy);
  };

  const selectedLines = Object.entries(selected).map(([sku, count]) => {
    const it = inv.find((i) => i.sku === sku);
    // offer_price = store selling price (incl. GST); MRP is filled from the SKU master by the backend
    return it ? { sku, count, product_name: it.product_name, barcode: it.barcode || sku, offer_price: it.selling_price || 0, mrp: it.mrp || 0 } : null;
  }).filter(Boolean);

  const printPdf = async () => {
    if (selectedLines.length === 0) return;
    const token = localStorage.getItem("pos_token");
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/barcodes/pdf`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ items: selectedLines }),
    });
    if (!res.ok) return alert("Failed to generate");
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };

  const totalStickers = selectedLines.reduce((a, l) => a + l.count, 0);

  return (
    <Shell>
      <PageHeader
        title="Barcode Printing"
        subtitle="Generate printable sticker sheets (A4, 3×8 = 24 per page, Code128)."
        testId="barcodes-header"
        actions={<button onClick={printPdf} disabled={totalStickers === 0} data-testid="print-barcodes-btn" className="inline-flex items-center gap-2 h-10 px-4 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold rounded-md disabled:opacity-50"><Printer size={16} weight="bold" />Print {totalStickers || ""} Stickers</button>}
      />
      <div className="p-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-3">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter SKU, name or barcode…" data-testid="barcode-filter" className="w-full h-11 px-4 border border-slate-300 bg-white rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
          <div className="bg-white border border-slate-200 rounded-md max-h-[70vh] overflow-y-auto">
            {filtered.length === 0 && <div className="text-center py-12 text-slate-400 text-sm">No inventory found.</div>}
            {filtered.map((s) => (
              <div key={s.sku} className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 hover:bg-slate-50" data-testid={`barcode-row-${s.sku}`}>
                <Barcode size={18} className="text-slate-400" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{s.product_name}</div>
                  <div className="text-[10px] text-slate-500 font-mono-num">{s.sku} · {s.barcode || "(no barcode)"}</div>
                </div>
                <div className="text-xs text-slate-500 font-mono-num">Stk {s.qty}</div>
                <div className="inline-flex items-center border border-slate-300 rounded-md overflow-hidden">
                  <button onClick={() => inc(s.sku, -1)} className="w-7 h-7 hover:bg-slate-100"><Minus size={12} className="mx-auto" /></button>
                  <span className="w-8 text-center text-sm font-mono-num font-semibold">{selected[s.sku] || 0}</span>
                  <button onClick={() => inc(s.sku, 1)} className="w-7 h-7 hover:bg-slate-100"><Plus size={12} className="mx-auto" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-2">Print Queue</div>
            {selectedLines.length === 0 && <div className="text-sm text-slate-400">Select products & quantities from the left.</div>}
            {selectedLines.map((l) => (
              <div key={l.sku} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-0">
                <span className="truncate flex-1">{l.product_name}</span>
                <span className="font-mono-num font-bold text-slate-900">×{l.count}</span>
                <button onClick={() => { const c = { ...selected }; delete c[l.sku]; setSelected(c); }} className="ml-2 p-1 text-red-600"><Trash size={13} /></button>
              </div>
            ))}
            {selectedLines.length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-200 flex items-baseline justify-between">
                <span className="font-semibold text-sm">Total Stickers</span>
                <span className="font-display font-bold text-xl font-mono-num">{totalStickers}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </Shell>
  );
}
